import { forwardRef, type ComponentPropsWithRef } from "react";
import { Slot } from "radix-ui";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TooltipIconButtonProps = ComponentPropsWithRef<typeof Button> & {
  tooltip: string;
  side?: "top" | "bottom" | "left" | "right";
  /** When true, the tooltip is shown only on keyboard focus, never on pointer hover.
   *  Use for the FAB launcher; leave false (default) for all other icon buttons. */
  focusOnly?: boolean;
};

export const TooltipIconButton = forwardRef<
  HTMLButtonElement,
  TooltipIconButtonProps
>(({ children, tooltip, side = "bottom", className, focusOnly = false, ...rest }, ref) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          {...rest}
          style={{ border: "none", outline: "none", boxShadow: "none", borderRadius: "6px", ...rest.style }}
          className={cn("aui-button-icon size-6 p-1 rounded-md border-0 outline-none ring-0 shadow-none bg-transparent hover:bg-muted focus:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:outline-none focus-visible:shadow-none", className)}
          ref={ref}
          // When focusOnly is set, block pointer-triggered tooltip opens while
          // preserving keyboard-focus tooltip behaviour. Compose with any
          // consumer-provided handlers so we don't silently drop them.
          onPointerMove={focusOnly ? (e) => { e.preventDefault(); rest.onPointerMove?.(e); } : rest.onPointerMove}
          onPointerLeave={focusOnly ? (e) => { e.preventDefault(); rest.onPointerLeave?.(e); } : rest.onPointerLeave}
        >
          <Slot.Slottable>{children}</Slot.Slottable>
          <span className="aui-sr-only sr-only">{tooltip}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side={side}>{tooltip}</TooltipContent>
    </Tooltip>
  );
});

TooltipIconButton.displayName = "TooltipIconButton";
